import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { useVerifiedDancer } from './hooks/useVerifiedDancer';
import { useFollows } from './hooks/useFollows';
import { getCachedDancerRankings, getCachedCategoryRankings } from './utils/globalCache';
import { bucketKey, sanitizeKey, categoryLabel, medalFor, ordinal, timeAgo } from './utils/engagementKeys';
import DancerThumbnail from './DancerThumbnail';
import './MyDashboard.css';

const DAY_MS = 86400000;
const EXPIRY_WARNING_DAYS = 45;

// What each badge needs – shown for the ones the dancer hasn't unlocked yet
const BADGE_GOALS = [
  { id: 'gold-medal', icon: '🥇', name: 'Gold Medal', how: 'Win 1st place in any competition' },
  { id: 'silver-medal', icon: '🥈', name: 'Silver Medal', how: 'Finish 2nd in any competition' },
  { id: 'bronze-medal', icon: '🥉', name: 'Bronze Medal', how: 'Finish 3rd in any competition' },
  { id: 'diverse', icon: '🎭', name: 'Diverse', how: 'Compete in more than one discipline' },
  { id: 'eternal-cup', icon: '🏆', name: 'Eternal Cup Winner', how: 'Win an Eternal Cup category' }
];

const toDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

/** Per-category numbers that turn a rank into a goal: who is above, the gap, expiring points. */
function buildGoal(overview, categoryDoc, dancerName) {
  const rankings = (categoryDoc && categoryDoc.rankings) || [];
  const me = rankings.find((r) => r.dancerName === dancerName);
  if (!me) return null;

  const sorted = [...rankings].sort((a, b) => b.totalPoints - a.totalPoints);
  const above = [...sorted].reverse().find((r) => r.totalPoints > me.totalPoints) || null; // closest above
  const below = sorted.find((r) => r.totalPoints < me.totalPoints) || null; // closest below
  const myRank = me.currentRank;

  // Points needed to enter the top 3 (strictly more than the 3rd place holder)
  const third = sorted.find((r) => r.currentRank === 3) || sorted[2] || null;
  const toTop3 = myRank > 3 && third ? third.totalPoints - me.totalPoints + 1 : null;

  // Results that count towards the score (top 6) and will fall out of the 365-day window soon
  const counting = new Set(me.top6CompIds || []);
  const now = Date.now();
  const expiring = (me.results || [])
    .filter((r) => counting.has(r.compId))
    .map((r) => {
      const d = toDate(r.resultDate || r.timestamp);
      return d ? { points: r.points || 0, place: r.place, expiresInDays: Math.ceil((d.getTime() + 365 * DAY_MS - now) / DAY_MS) } : null;
    })
    .filter((r) => r && r.expiresInDays <= EXPIRY_WARNING_DAYS && r.expiresInDays >= 0)
    .sort((a, b) => a.expiresInDays - b.expiresInDays);

  return {
    label: categoryLabel(overview),
    rank: myRank,
    totalDancers: categoryDoc.totalDancers || rankings.length,
    points: me.totalPoints,
    daysHeld: me.currentDaysHeld || 0,
    bestRank: me.bestRank || null,
    above: above ? { name: above.dancerName, rank: above.currentRank, gap: above.totalPoints - me.totalPoints + 1 } : null,
    below: below ? { name: below.dancerName, lead: me.totalPoints - below.totalPoints } : null,
    toTop3,
    expiring,
    expiringPoints: expiring.reduce((a, r) => a + r.points, 0)
  };
}

function Goal({ g }) {
  return (
    <div className="md-goal">
      <div className="md-goal-head">
        <div>
          <div className="md-goal-label">{g.label}</div>
          <div className="md-goal-sub">{g.points} pts · {g.daysHeld} day{g.daysHeld === 1 ? '' : 's'} at this rank</div>
        </div>
        <div className="md-rank">
          <span className="md-rank-num">#{g.rank}</span>
          <span className="md-rank-of">of {g.totalDancers}</span>
        </div>
      </div>

      <ul className="md-goal-list">
        {g.rank === 1 && <li>👑 You&apos;re <strong>#1</strong>{g.below ? <> — {g.below.lead} pts clear of <Link to={`/dancer/${encodeURIComponent(g.below.name)}`}>{g.below.name}</Link></> : null}.</li>}
        {g.above && (
          <li>
            🎯 <strong>{g.above.gap} pts</strong> to overtake <Link to={`/dancer/${encodeURIComponent(g.above.name)}`}>{g.above.name}</Link> (#{g.above.rank})
          </li>
        )}
        {g.toTop3 != null && g.toTop3 > 0 && g.rank > 3 && <li>⭐ <strong>{g.toTop3} pts</strong> to reach the top 3</li>}
        {g.below && g.rank !== 1 && g.below.lead <= 40 && (
          <li>🛡️ Only {g.below.lead} pts ahead of <Link to={`/dancer/${encodeURIComponent(g.below.name)}`}>{g.below.name}</Link> — defend your spot!</li>
        )}
        {g.expiring.length > 0 && (
          <li className="md-warn">
            ⏳ <strong>{g.expiringPoints} pts</strong> expire {g.expiring[0].expiresInDays === 0 ? 'today' : `in ${g.expiring[0].expiresInDays} day${g.expiring[0].expiresInDays === 1 ? '' : 's'}`}
            {g.expiring.length > 1 ? ` (and more within ${EXPIRY_WARNING_DAYS} days)` : ''} — time to compete again!
          </li>
        )}
      </ul>
    </div>
  );
}

export default function MyDashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading, isVerified, verifiedDancerName } = useVerifiedDancer();
  const follows = useFollows();
  const [state, setState] = useState({ loading: true, goals: [], badges: [], feed: [], predictions: null, followers: 0, studio: null });

  useEffect(() => {
    if (!isVerified || !verifiedDancerName) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const [cache, feedSnap, predSnap, followerSnap] = await Promise.all([
          getCachedDancerRankings(verifiedDancerName),
          getDoc(doc(db, 'feedBuckets', bucketKey('dancer', verifiedDancerName))).catch(() => null),
          getDoc(doc(db, 'dancerPredictionStats', sanitizeKey(verifiedDancerName))).catch(() => null),
          getDoc(doc(db, 'followerCounts', bucketKey('dancer', verifiedDancerName))).catch(() => null)
        ]);

        const overviews = ((cache && cache.disciplineOverview) || []).filter((o) => o.currentRank);
        const categoryDocs = await Promise.all(
          overviews.map((o) => getCachedCategoryRankings(o.discipline, o.category, o.dance || null))
        );
        const goals = overviews
          .map((o, i) => buildGoal(o, categoryDocs[i], verifiedDancerName))
          .filter(Boolean)
          .sort((a, b) => a.rank - b.rank);

        if (cancelled) return;
        setState({
          loading: false,
          goals,
          badges: (cache && cache.achievementBadges) || [],
          feed: feedSnap && feedSnap.exists() ? (feedSnap.data().events || []).slice(0, 6) : [],
          predictions: predSnap && predSnap.exists() ? predSnap.data() : null,
          followers: followerSnap && followerSnap.exists() ? Math.max(0, followerSnap.data().count || 0) : 0,
          studio: cache && cache.studio
        });
      } catch (e) {
        console.error('Dashboard load failed:', e);
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
      }
    })();

    return () => { cancelled = true; };
  }, [isVerified, verifiedDancerName]);

  if (authLoading) return <div className="md-page"><p>Loading…</p></div>;

  if (!user) {
    return (
      <div className="md-page">
        <h2>👤 My Dashboard</h2>
        <div className="md-card md-center">
          <p>Sign in to see your personal dashboard.</p>
          <button className="md-primary" onClick={() => navigate('/login')}>Login / Sign Up</button>
        </div>
      </div>
    );
  }

  if (!isVerified) {
    return (
      <div className="md-page">
        <h2>👤 My Dashboard</h2>
        <div className="md-card md-center">
          <p>Your dashboard is for <strong>verified dancers</strong> — claim your profile and, once it&apos;s approved, you&apos;ll see your rank goals, expiring points, badges and prediction score here.</p>
          <button className="md-primary" onClick={() => navigate('/search')}>Find my profile</button>
        </div>
      </div>
    );
  }

  const { loading, goals, badges, feed, predictions, followers, studio } = state;
  const earned = new Set(badges.map((b) => b.id));
  const locked = BADGE_GOALS.filter((b) => !earned.has(b.id));
  const totalExpiring = goals.reduce((a, g) => a + g.expiringPoints, 0);

  return (
    <div className="md-page">
      <div className="md-hero">
        <DancerThumbnail dancerName={verifiedDancerName} size={72} />
        <div className="md-hero-info">
          <h2>{verifiedDancerName} <span className="md-verified">✅ Verified</span></h2>
          <div className="md-hero-sub">
            {studio && <span>🏛️ {studio}</span>}
            <span>👥 {followers} follower{followers === 1 ? '' : 's'}</span>
            <span>★ Following {follows.dancers.length + follows.studios.length}</span>
          </div>
        </div>
        <Link className="md-link-btn" to={`/dancer/${encodeURIComponent(verifiedDancerName)}`}>View public profile</Link>
      </div>

      {loading ? <p>Loading your stats…</p> : (
        <>
          {totalExpiring > 0 && (
            <div className="md-alert">
              ⏳ <strong>{totalExpiring} points</strong> will drop out of your ranking in the next {EXPIRY_WARNING_DAYS} days. Enter a competition to keep your place.
              <Link to="/">See upcoming competitions →</Link>
            </div>
          )}

          <section>
            <h3>🎯 Your ranking goals</h3>
            {goals.length === 0 ? (
              <div className="md-card md-muted">No active rankings yet — your goals appear after your first ranked result.</div>
            ) : (
              <div className="md-grid">{goals.map((g) => <Goal key={g.label} g={g} />)}</div>
            )}
          </section>

          <div className="md-two">
            <section className="md-card">
              <h3>🏅 Badges</h3>
              {badges.length === 0 && <p className="md-muted">No badges yet.</p>}
              <ul className="md-badges">
                {badges.map((b) => (
                  <li key={b.id} title={b.description}>
                    <span className="md-badge-icon">{b.icon}</span>
                    <span>{b.name}{b.count ? ` ×${b.count}` : ''}</span>
                  </li>
                ))}
              </ul>
              {locked.length > 0 && (
                <>
                  <h4>Up next</h4>
                  <ul className="md-locked">
                    {locked.map((b) => (
                      <li key={b.id}><span className="md-badge-icon">{b.icon}</span><span><strong>{b.name}</strong> — {b.how}</span></li>
                    ))}
                  </ul>
                </>
              )}
            </section>

            <section className="md-card">
              <h3>🔮 Prediction points</h3>
              {predictions && predictions.total ? (
                <>
                  <div className="md-big">{predictions.points}</div>
                  <p className="md-muted">{predictions.points} correct of {predictions.total} predictions. See details on your public profile.</p>
                </>
              ) : (
                <p className="md-muted">Predict the winners on a competition card on the <Link to="/">homepage</Link> — every correct pick earns a point on your profile.</p>
              )}
            </section>
          </div>

          <section className="md-card">
            <div className="md-row-head">
              <h3>🕘 Recent activity</h3>
              <Link to="/my-feed">My Feed →</Link>
            </div>
            {feed.length === 0 ? <p className="md-muted">Your new results and rank moves will show up here.</p> : (
              <ul className="md-activity">
                {feed.map((ev) => (
                  <li key={ev.id}>
                    {ev.type === 'result' ? (
                      <>{medalFor(ev.place)} <strong>{ordinal(ev.place)}</strong> in {ev.label} — {ev.competitionName}</>
                    ) : (
                      <>{ev.fromRank && ev.fromRank > ev.toRank ? '📈' : ev.fromRank ? '📉' : '⭐'} #{ev.fromRank || '–'} → <strong>#{ev.toRank}</strong> in {ev.label}</>
                    )}
                    <span className="md-time">{timeAgo(ev.ts)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <div className="md-links">
            <Link to="/notifications">🔔 Notification settings</Link>
            <Link to="/compare-dancers">🆚 Compare with a rival</Link>
            <Link to="/points-calculator">🧮 Points calculator</Link>
          </div>
        </>
      )}
    </div>
  );
}
